import { useEffect, useRef, useState } from 'react';
import { Dialog } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { useAnnouncer } from '../../components/Status';
import type { MemoryAnchor } from '../../domain/types';
import { useI18n } from '../../i18n';
import { formatDuration } from '../../lib/time';
import { uuid } from '../../lib/uuid';
import { useRepos } from '../../repositories';
import { useApp } from '../../app/AppState';

/** Long enough for anything anyone wants to say about one day; short enough to hold in memory. */
const MAX_MS = 5 * 60 * 1000;

/**
 * *Say something about this day* — a voice note, recorded here and kept as an
 * ordinary Moment.
 *
 * A recording about a wedding is the same kind of thing as a photograph of it:
 * something a relative added to a day the family remembers. So it takes exactly
 * the same path — `photos.add` with an `audio/…` blob and `addedTo` naming the
 * day — and arrives in the same three places as every picture: inside the day,
 * in the Moments tab, and under the circle of whoever recorded it.
 *
 * It is deliberately *not* the story recorder (features/stories/useRecorder):
 * that one writes chunks to durable storage as they arrive, because a story is
 * an hour of a grandmother's life and losing it would be a tragedy. This is a
 * sentence about a photograph. It is held in memory, and if the tab closes
 * mid-sentence the sentence is said again.
 */
export function VoiceNoteDialog({ anchor, open, onClose, label }: { anchor: MemoryAnchor; open: boolean; onClose: () => void;
  /** What this is called where it is offered — "about this day" is wrong over a recipe. */
  label?: string;
}) {
  const { t } = useI18n();
  const repos = useRepos();
  const { toast } = useAnnouncer();
  const { reload } = useApp();
  const [state, setState] = useState<'idle' | 'asking' | 'recording' | 'ready' | 'saving' | 'denied'>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [clip, setClip] = useState<{ blob: Blob; url: string; ms: number } | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAt = useRef(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const support = repos.media.recordingSupport();

  const stopEverything = () => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
    recorderRef.current = null;
  };

  // Closing the sheet mid-sentence must release the microphone, not leave it live.
  useEffect(() => {
    if (open) return;
    stopEverything();
    setState('idle');
    setElapsed(0);
    setClip((c) => {
      if (c) URL.revokeObjectURL(c.url);
      return null;
    });
  }, [open]);

  useEffect(() => () => stopEverything(), []);

  const start = async () => {
    if (!support.supported) return;
    setState('asking');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream, support.mimeType ? { mimeType: support.mimeType } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const ms = Date.now() - startedAt.current;
        const blob = new Blob(chunksRef.current, { type: support.mimeType || 'audio/webm' });
        stopEverything();
        setClip({ blob, url: URL.createObjectURL(blob), ms });
        setState('ready');
      };
      startedAt.current = Date.now();
      rec.start();
      setState('recording');
      timer.current = setInterval(() => {
        const ms = Date.now() - startedAt.current;
        setElapsed(ms);
        if (ms >= MAX_MS) rec.stop();
      }, 200);
    } catch {
      stopEverything();
      setState('denied');
    }
  };

  const stop = () => recorderRef.current?.stop();

  const again = () => {
    if (clip) URL.revokeObjectURL(clip.url);
    setClip(null);
    setElapsed(0);
    setState('idle');
  };

  const save = async () => {
    if (!clip) return;
    setState('saving');
    const res = await repos.photos.add({ image: clip.blob, durationMs: clip.ms, isEvent: false, addedTo: anchor, idempotencyKey: uuid() });
    if (res.ok) {
      toast(t('voice.saved'));
      reload();
      onClose();
    } else {
      toast(t('memories.addFailed', { n: 1 }));
      setState('ready');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={label ?? t('voice.title')} labelledBy="voice-title">
      <div className="stack voice-note">
        <p className="subtle-hint" style={{ marginTop: 0 }}>{t('voice.help')}</p>

        {!support.supported ? (
          <p className="muted">{support.reason === 'insecure' ? t('record.unsupported.insecure') : t('record.unsupported.no_api')}</p>
        ) : state === 'denied' ? (
          <p className="muted">{t('record.permissionHelp')}</p>
        ) : null}

        <div className="voice-stage">
          <span className={`voice-orb ${state === 'recording' ? 'is-live' : ''}`} aria-hidden="true">
            <Icon name="mic" />
          </span>
          <span className="voice-time" aria-live="polite">
            {formatDuration(state === 'ready' && clip ? clip.ms : elapsed)}
          </span>
        </div>

        {clip && <audio className="voice-player" src={clip.url} controls preload="metadata" />}

        <div className="row voice-actions">
          {state === 'recording' ? (
            <button type="button" className="btn btn-primary btn-lg btn-block" onClick={stop}>
              <Icon name="stop" />
              <span>{t('voice.stop')}</span>
            </button>
          ) : state === 'ready' ? (
            <>
              <button type="button" className="btn btn-primary btn-lg grow" onClick={() => void save()} disabled={state !== 'ready'}>
                <Icon name="check" />
                <span>{t('voice.save')}</span>
              </button>
              <button type="button" className="btn btn-ghost btn-lg" onClick={again}>
                <Icon name="refresh" />
                <span>{t('voice.again')}</span>
              </button>
            </>
          ) : state === 'saving' ? (
            <button type="button" className="btn btn-primary btn-lg btn-block" disabled>
              <span>{t('common.saving')}</span>
            </button>
          ) : (
            <button type="button" className="btn btn-primary btn-lg btn-block" onClick={() => void start()} disabled={state === 'asking' || !support.supported}>
              <Icon name="mic" />
              <span>{state === 'asking' ? t('record.permissionTitle') : t('voice.start')}</span>
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
